import React, {useCallback, useMemo} from 'react';
import * as THREE from 'three';
import {useAppSelector} from '../../app/useAppSelector';
import {useAppDispatch} from '../../app/useAppDispatch';
import {rtlsActions} from '@shared/slices/rtlsSlice';
import {AssetMarker} from './AssetMarker';
import {useAssetModels} from './useAssetModels';
import {useGroundSnap} from '../hooks/useGroundSnap';
import {usePlaybackPositions} from './usePlaybackPositions';
import {useMarkerActions} from './useMarkerActions';
import type {RTLSMarkerData} from './markerUtils';

// TODO M3: playback animation — removed playingAssetsRef, playbackPositionsRef,
// playbackDirectionsRef, playingIds state, handlePlayTrail, playingTrailPath,
// TrailPathLine, and the useFrame animation loop. Re-add in M3.

interface RTLSOverlayProps {
  showMarkers?: boolean;
  showLabels?: boolean;
}

export const RTLSOverlay: React.FC<RTLSOverlayProps> = ({
  showMarkers = true,
  showLabels = true,
}) => {
  const dispatch = useAppDispatch();
  const {onMarkerDoubleClick} = useMarkerActions();

  // Redux selectors replace all eventBus subscriptions
  const assetsRecord = useAppSelector(s => s.rtls.assets);
  const selectedAssetId = useAppSelector(s => s.rtls.selectedAssetId);
  const filterLevel = useAppSelector(s => s.rtls.filterLevel);
  const playbackMode = useAppSelector(s => s.rtls.playbackMode);
  const panelFilter = useAppSelector(s => s.rtls.panelFilter);

  // Mobile-parity isolation: when a user is in playback mode with a selected
  // asset, hide every other asset. Matches webview RTLSOverlay line 391
  // (`if (isAnyPlaying && !isPlaying) return null`). On playback completion,
  // PlaybackAdvancer flips back to live mode → isolation lifts → all assets
  // reappear.
  const isolatingPlayback = playbackMode === 'playback' && selectedAssetId != null;
  const isPlaybackMode = playbackMode === 'playback';

  // TODO: move to viewerSlice when a UI toggle lands
  const use3DModels = true;

  const assetModels = useAssetModels();
  const {snapToGround} = useGroundSnap(2);
  const playbackPositions = usePlaybackPositions();

  // Derive flat marker array from Redux record, substituting interpolated
  // playback positions when in playback mode.
  const assets = useMemo<RTLSMarkerData[]>(
    () =>
      Object.values(assetsRecord).map(asset => ({
        id: asset.id,
        name: asset.name,
        externalId: asset.externalId,
        type: asset.type,
        subType: asset.subType,
        status: asset.status,
        position: playbackPositions.get(asset.id) ?? asset.position,
        // Equipment shows its asset_id ('HT102') — operationally how trucks
        // are tracked. Personnel shows the human name — employee_id is
        // useful in the panel but the name reads better above a person on
        // the map. Falls back to `name` when externalId is missing.
        label: asset.type === 'equipment'
          ? (asset.externalId ?? asset.name)
          : asset.name,
        level: asset.level,
      })),
    [assetsRecord, playbackPositions],
  );

  // Pre-snap all asset positions once when assets change (not during render).
  // Exception: the selected asset during playback isolation is NOT re-snapped.
  // The playback position comes from usePlaybackPositions, which interpolates
  // between recorded trail points that were already at their correct Z when
  // recorded. Re-snapping through useGroundSnap's 5-unit XY cache introduces
  // vertex-quantization Z hops as the asset crosses cell boundaries mid-move.
  const snappedAssets = useMemo(() => {
    const result = new Map<string, RTLSMarkerData>();
    for (const asset of assets) {
      const isPlayingAsset =
        isolatingPlayback && asset.id === selectedAssetId;
      const snappedPos = isPlayingAsset
        ? asset.position
        : snapToGround(asset.position.x, asset.position.y, asset.position.z);
      result.set(asset.id, {...asset, position: snappedPos});
    }
    return result;
  }, [assets, snapToGround, isolatingPlayback, selectedAssetId]);

  const handleMarkerClick = useCallback(
    (assetId: string) => {
      dispatch(rtlsActions.selectAsset(assetId));
    },
    [dispatch],
  );

  // Level filter + panel-type filter. Panel choice gates which asset
  // markers render on the map so the scene stays in sync with the
  // right-panel list (Personnel filter → only personnel markers; etc).
  // 'all' → both types; non-asset filters → nothing here.
  const visibleAssets = useMemo(() => {
    const all = Array.from(snappedAssets.values());
    const levelMatched = filterLevel === 'all'
      ? all
      : all.filter(a => a.level === filterLevel);
    if (panelFilter === 'all') return levelMatched;
    if (panelFilter === 'personnel' || panelFilter === 'equipment') {
      return levelMatched.filter(a => a.type === panelFilter);
    }
    // Non-asset filter (location / access_point / chamber) → hide all assets.
    return [];
  }, [snappedAssets, filterLevel, panelFilter]);

  // Spread co-located markers side by side
  const spreadData = useMemo(() => {
    const THRESHOLD = 20;
    const SPACING = 12;
    const MAX_VIS = 4;

    const sorted = [...visibleAssets].sort((a, b) => a.id.localeCompare(b.id));
    const assigned = new Set<string>();
    const groups: RTLSMarkerData[][] = [];

    for (const asset of sorted) {
      if (assigned.has(asset.id)) continue;
      const group = [asset];
      assigned.add(asset.id);
      for (const other of sorted) {
        if (assigned.has(other.id)) continue;
        const dx = asset.position.x - other.position.x;
        const dy = asset.position.y - other.position.y;
        if (Math.sqrt(dx * dx + dy * dy) < THRESHOLD) {
          group.push(other);
          assigned.add(other.id);
        }
      }
      groups.push(group);
    }

    const positions = new Map<string, {x: number; y: number; z: number}>();
    const clustered = new Set<string>();
    const hidden = new Set<string>();
    const clusters: Array<{
      id: string;
      center: {x: number; y: number; z: number};
      names: string[];
      overflow: number;
      badgePosition: {x: number; y: number; z: number};
    }> = [];

    for (const group of groups) {
      if (group.length === 1) {
        positions.set(group[0].id, group[0].position);
        continue;
      }

      const cx = group.reduce((s, a) => s + a.position.x, 0) / group.length;
      const cy = group.reduce((s, a) => s + a.position.y, 0) / group.length;
      const cz = group.reduce((s, a) => s + a.position.z, 0) / group.length;

      const vis = group.slice(0, MAX_VIS);
      const overflow = group.length - MAX_VIS;

      for (let i = 0; i < vis.length; i++) {
        const offsetX = (i - (vis.length - 1) / 2) * SPACING;
        positions.set(vis[i].id, {x: cx + offsetX, y: cy, z: cz});
        clustered.add(vis[i].id);
      }

      for (let i = MAX_VIS; i < group.length; i++) {
        hidden.add(group[i].id);
      }

      const badgeX = cx + ((vis.length - 1) / 2 + 1) * SPACING;
      clusters.push({
        id: group[0].id + '_cluster',
        center: {x: cx, y: cy, z: cz},
        names: group.map(a => a.label || a.name),
        overflow,
        badgePosition: {x: badgeX, y: cy, z: cz + 5},
      });
    }

    return {positions, clusters, clustered, hidden};
  }, [visibleAssets]);

  if (!showMarkers) {
    return null;
  }

  return (
    <>
      {visibleAssets.map(asset => {
        // Playback isolation: only the selected asset renders. During
        // isolation we ALSO bypass the spread/hidden logic for that asset
        // — the world-proximity grouping (THRESHOLD=20 / MAX_VIS=4) can
        // otherwise hide the selected asset when nearby personnel cross
        // its path during playback. Alphabetical sort puts equipment
        // after personnel IDs, so a 5-member cluster pushes the equipment
        // past MAX_VIS and the chase-camera target blinks out for a frame.
        if (isolatingPlayback && asset.id !== selectedAssetId) return null;
        if (!isolatingPlayback && spreadData.hidden.has(asset.id)) return null;

        const spreadPos = isolatingPlayback
          ? asset.position
          : (spreadData.positions.get(asset.id) || asset.position);
        const isInCluster = !isolatingPlayback && spreadData.clustered.has(asset.id);

        // Look up 3D model only when use3DModels is enabled
        let model3D: THREE.Object3D | null = null;
        let modelScale = 1;
        if (use3DModels) {
          const subTypeModel = asset.type === 'equipment' && asset.subType
            ? assetModels.equipment.get(asset.subType)
            : null;
          model3D = asset.type === 'equipment'
            ? (subTypeModel?.model || null)
            : (assetModels.personnel?.model || null);
          modelScale = asset.type === 'equipment'
            ? (subTypeModel?.scale || 1)
            : (assetModels.personnel?.scale || 1);
        }

        return (
          <AssetMarker
            key={asset.id}
            data={{...asset, position: spreadPos}}
            highlighted={asset.id === selectedAssetId}
            showLabel={showLabels && !isInCluster}
            onClick={handleMarkerClick}
            onDoubleClick={onMarkerDoubleClick}
            dimmed={false}
            model3D={model3D}
            modelScale={modelScale}
            isPlayback={isPlaybackMode && asset.id === selectedAssetId}
          />
        );
      })}

      {/* Cluster overflow badges and combined labels */}
      {spreadData.clusters.map(cluster => (
        <React.Fragment key={cluster.id}>
          {cluster.overflow > 0 && (
            <OverflowBadge count={cluster.overflow} position={cluster.badgePosition} />
          )}
          <ClusterLabel
            names={cluster.names}
            overflow={cluster.overflow}
            position={cluster.center}
            zOffset={5 + 18 / 2 + 5}
          />
        </React.Fragment>
      ))}
    </>
  );
};

RTLSOverlay.displayName = 'RTLSOverlay';

// Overflow badge — small circle showing "+N more" for hidden cluster members
const OverflowBadge: React.FC<{
  count: number;
  position: {x: number; y: number; z: number};
}> = React.memo(({count, position}) => {
  const {texture} = useMemo(() => {
    const sz = 256;
    const canvas = document.createElement('canvas');
    canvas.width = sz;
    canvas.height = sz;
    const ctx = canvas.getContext('2d')!;
    const cx = sz / 2, cy = sz / 2;
    const radius = sz * 0.42;
    const borderWidth = sz * 0.035;

    ctx.clearRect(0, 0, sz, sz);

    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetY = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fillStyle = '#555555';
    ctx.fill();
    ctx.restore();

    ctx.beginPath();
    ctx.arc(cx, cy, radius - borderWidth, 0, Math.PI * 2);
    ctx.fillStyle = '#333333';
    ctx.fill();

    ctx.fillStyle = '#E6E6E6';
    ctx.font = `bold ${radius * 0.5}px -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`+${count}`, cx, cy - radius * 0.1);

    ctx.font = `${radius * 0.28}px -apple-system, sans-serif`;
    ctx.fillText('more', cx, cy + radius * 0.3);

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.needsUpdate = true;
    return {texture: tex};
  }, [count]);

  const material = useMemo(() => {
    const mat = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      sizeAttenuation: true,
    });
    mat.toneMapped = false;
    return mat;
  }, [texture]);

  return (
    <sprite
      material={material}
      position={[position.x, position.y, position.z]}
      scale={[16, 16, 1]}
    />
  );
});

OverflowBadge.displayName = 'OverflowBadge';

// Combined label for clustered markers
const ClusterLabel: React.FC<{
  names: string[];
  overflow: number;
  position: {x: number; y: number; z: number};
  zOffset: number;
}> = React.memo(({names, overflow, position, zOffset}) => {
  const {texture, aspect} = useMemo(() => {
    const MAX_NAMES = 4;
    const visibleNames = names.slice(0, MAX_NAMES);
    let text = visibleNames.join(', ');
    if (overflow > 0) {
      text += `, ${overflow} more`;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const s = 4;
    const fontSize = 22 * s;
    const paddingH = 20 * s;
    const paddingV = 12 * s;

    ctx.font = `600 ${fontSize}px Inter, -apple-system, BlinkMacSystemFont, sans-serif`;
    const textW = ctx.measureText(text).width;
    const totalW = paddingH + textW + paddingH;
    const totalH = fontSize + paddingV * 2;
    const borderRadius = totalH / 2;

    canvas.width = totalW;
    canvas.height = totalH;

    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 8 * s;
    ctx.shadowOffsetY = 2 * s;
    ctx.beginPath();
    ctx.roundRect(0, 0, totalW, totalH, borderRadius);
    ctx.fillStyle = 'rgba(30, 36, 51, 0.85)';
    ctx.fill();
    ctx.restore();

    ctx.beginPath();
    ctx.roundRect(0, 0, totalW, totalH, borderRadius);
    ctx.fillStyle = 'rgba(30, 36, 51, 0.85)';
    ctx.fill();

    ctx.beginPath();
    ctx.roundRect(0, 0, totalW, totalH, borderRadius);
    ctx.strokeStyle = 'rgba(75, 85, 99, 0.5)';
    ctx.lineWidth = 1.5 * s;
    ctx.stroke();

    ctx.fillStyle = '#FFFFFF';
    ctx.font = `600 ${fontSize}px Inter, -apple-system, BlinkMacSystemFont, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, totalW / 2, totalH / 2);

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.needsUpdate = true;
    return {texture: tex, aspect: totalW / totalH};
  }, [names, overflow]);

  const spriteHeight = 5;
  const spriteWidth = spriteHeight * aspect;

  const material = useMemo(() => {
    const mat = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      sizeAttenuation: true,
    });
    mat.toneMapped = false;
    return mat;
  }, [texture]);

  return (
    <sprite
      material={material}
      position={[position.x, position.y, position.z + zOffset]}
      scale={[spriteWidth, spriteHeight, 1]}
    />
  );
});

ClusterLabel.displayName = 'ClusterLabel';
